import { DislikeOutlined, LikeOutlined, ReloadOutlined } from "@ant-design/icons";
import type React from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { EventType, trackEvent } from "../../../utils/analytics";
import {
  type PrechartRefreshResult,
  fetchPrechartLabValues,
  openPrechartOriginal,
  prechartBaseUrl,
  requestPrechartRefresh,
  sendPrechartFeedback,
} from "../prechart";
import {
  type Day,
  type DayItem,
  MAX_DAY_ROWS,
  MAX_PENDING,
  type Tile,
  type TileKey,
  buildTiles,
  buildTimeline,
  dayKey,
  rowName,
  tileNoun,
  tilesOf,
} from "./preVisitBriefModel";

// The pre-visit brief card: count tiles, the summary, the labs, the timeline
// since the last visit and the questions (payload: docs/precharting/api-contract.md).
// Every patient gets the same card in the same order; only the numbers change.
export interface BriefSource {
  // The fact id ("result:<id>", "doc:<id>", "enc:<id>", "outside:<key>",
  // "outside-proc:<key>", "note:<audiofile id>"), as the harness's Source.ref.
  ref: string;
  kind?: "result" | "document" | "encounter" | "outside_result" | "outside_procedure" | "note" | string;
  label: string;
  unreviewed?: boolean;
  detail?: string | null;
  // A document's gist in parts (absent on older briefs: `detail` has it whole).
  summary?: string | null;
  findings?: string[] | null;
  plan?: string[] | null;
}

// The stored file a row can open in a new tab: "pdf", "fax" (a scan served
// as a PDF) or "image".
export interface BriefOriginal {
  media: "pdf" | "fax" | "image" | string;
  pages?: number | null;
}

// One fact in a fold, listed when the fold unfolds.
export interface BriefFoldMember {
  ref: string;
  kind?: string;
  title: string;
  date?: string | null;
  sub?: string | null;
  unreviewed?: boolean;
  read?: boolean;
  identity_mismatch?: boolean;
  unread_line?: string | null;
  doc_kind?: string | null;
  original?: BriefOriginal | null;
}

export interface BriefCoverage {
  complete?: boolean;
  gaps?: string[];
}

export interface BriefAnchor {
  date?: string | null;
  visit_type?: string | null;
  days_ago?: number | null;
  // "provider": this clinician's own last office visit; "practice": the practice's.
  scope?: "provider" | "practice" | string | null;
  note_signed?: boolean | null;
  // The bundle capped a very old anchor to its maximum window, which spans
  // `window_days` (days from the window start to the appointment).
  window_capped?: boolean | null;
  window_days?: number | null;
}

// One fact (or a fold of many) on the timeline.
export interface BriefEvent {
  ref: string;
  kind:
    | "result"
    | "pending"
    | "document"
    | "telephone"
    | "office"
    | "other_visit"
    | "outside_result"
    | "outside_procedure"
    | "fold"
    | string;
  date?: string | null;
  arrived?: string | null;
  title: string;
  // result and pending: the order's name.
  name?: string | null;
  // result and outside result: "lab", "imaging", "procedure" or "other".
  category?: string | null;
  // result, outside result and pending: the panel's short name ("CBC").
  short?: string | null;
  sub?: string | null;
  flagged?: boolean;
  unreviewed?: boolean;
  other_provider?: boolean;
  older_care?: boolean;
  open?: boolean;
  read?: boolean;
  identity_mismatch?: boolean;
  count?: number | null;
  sources?: BriefSource[];
  // A document: its type in a word or two ("Discharge summary"), its stored
  // file, and why it wasn't read ("Not downloaded · eCW gave no file type").
  doc_kind?: string | null;
  original?: BriefOriginal | null;
  unread_line?: string | null;
  // A fold: what it stands for, in order.
  members?: BriefFoldMember[] | null;
}

// One value of a lab panel; `flag` is the EHR's ("H", "L", …), "" when it did not flag it.
export interface BriefLabValue {
  name: string;
  short?: string;
  value: string;
  units?: string;
  range?: string;
  flag?: string;
  // A report (an interpretation), not a number: wrapping text, never a chip.
  narrative?: boolean;
}

// A lab panel for the Labs section.
export interface BriefLab {
  ref: string;
  name: string;
  short?: string;
  date?: string | null;
  arrived?: string | null;
  // An outside (Prisma) panel.
  outside?: boolean;
  unreviewed?: boolean;
  // The EHR marked the panel abnormal (it may flag no single value).
  marked_abnormal?: boolean;
  // False: the values were not read.
  read?: boolean;
  // Flagged first.
  values?: BriefLabValue[];
  // How many values the EHR returned, and flagged (more than `values` when capped).
  // null: unknown (an older brief cut at 25 values; `truncated` is then true).
  value_total?: number | null;
  flagged_total?: number | null;
  // Values present and not flagged by the EHR ("not flagged", never "normal").
  unflagged_total?: number | null;
  truncated?: boolean;
  // A result with no discrete values reports in text.
  text?: string;
  // The vendor's scanned report for this panel, when one matches.
  report?: { ref: string; title?: string | null; pages?: number | null; media?: string | null } | null;
}

// A deterministic flag row. The card no longer shows these (it presents, it
// doesn't rank); Superdoc's context still reads them.
export interface BriefFlag {
  ref: string;
  kind: string;
  date?: string | null;
  priority?: string;
  headline?: string | null;
  detail?: string | null;
  unreviewed?: boolean;
  outside?: boolean;
  sources?: BriefSource[];
}

// The prior note the morning pull imported for this patient.
export interface BriefPriorNote {
  available?: boolean;
  source?: string | null;
  service_date?: string | null;
  // False: the newest note on file is not the anchor visit's.
  matches_anchor?: boolean | null;
  // The model's, from the note text (empty without a model or a note).
  plan_phrases?: string[] | null;
  summary?: string | null;
  ai?: boolean;
  // The note's fact id ("note:<id>"): its full text opens in a new tab.
  ref?: string | null;
}

// The anchor visit as the timeline's last row.
export interface BriefLastVisit {
  date?: string | null;
  visit_type?: string | null;
  mine?: boolean | null;
  orders_text?: string | null;
  note?: BriefPriorNote | null;
  // The visit's note as eCW prints it ("pn:<encounter id>"), a PDF: what the
  // Note chip opens when present (else the note's text).
  original?: { ref: string; media?: string | null; pages?: number | null } | null;
  sources?: BriefSource[];
}

export interface BriefQuestion {
  text: string;
  ai?: boolean;
  sources?: BriefSource[];
}

// One run of the written summary. `attention` runs are the tappable ones
// (they jump to their fact on the card); `warn` (set by the host) marks a
// document whose identity check failed.
export interface BriefSummarySegment {
  text: string;
  attention?: boolean;
  warn?: boolean;
  sources?: BriefSource[];
}

// The model's written summary; null ⇒ the plain headline instead.
export interface BriefSummary {
  segments?: BriefSummarySegment[] | null;
  ai?: boolean;
  uncovered?: BriefFlag[] | null;
}

export interface PreVisitBriefData {
  brief_id: string;
  appointment?: { date?: string | null; time?: string | null; visit_type?: string | null } | null;
  anchor?: BriefAnchor | null;
  is_new_patient?: boolean;
  // The plain one-line count ("4 results, 2 documents and 1 call since …").
  headline?: string | null;
  composer?: "llm" | "deterministic" | string;
  summary?: BriefSummary | null;
  flags?: BriefFlag[] | null;
  flags_total?: number | null;
  last_visit?: BriefLastVisit | null;
  questions?: BriefQuestion[] | null;
  events?: BriefEvent[] | null;
  // Absent on briefs built before the Labs section: lab rows stay timeline rows.
  labs?: BriefLab[] | null;
  coverage?: BriefCoverage | null;
}

interface PreVisitBriefProps {
  data: PreVisitBriefData;
  // The patient the panel is open on — Refresh queues a pre-chart for them.
  // Absent (no patient selected) ⇒ no Refresh.
  clientId?: number;
  // A newer brief is further down the thread: read-only, no actions.
  frozen?: boolean;
  // Sends a "Worth asking" question to the chat; absent ⇒ the questions are text.
  onAsk?: (question: string) => void;
  // The chat is answering: questions wait.
  askDisabled?: boolean;
}

// "2026-09-30" as a local calendar date (never shifted by the timezone).
const parseDay = (iso?: string | null): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// "Wed, Sep 30"
export const appointmentDayLabel = (iso?: string | null): string => {
  const d = parseDay(iso);
  return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : iso || "";
};

// "Jul 5", with the year when it isn't the reference year ("Jul 5, 2025").
export const lastVisitDayLabel = (iso?: string | null, referenceYear?: number): string => {
  const d = parseDay(iso);
  if (!d) return iso || "";
  const year = referenceYear ?? new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== year ? { year: "numeric" } : {}),
  });
};

export const daysAgoLabel = (days?: number | null): string => {
  if (days == null || Number.isNaN(days)) return "";
  if (days <= 0) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

// "2026-09-12" → "Sep 12" (with the year when it isn't the visit's) wherever the
// backend wrote an ISO date into text: it keeps ISO so Superdoc's context stays
// unambiguous; the card reads dates the way its header does.
const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
const prettyDates = (text: string, referenceYear?: number): string =>
  text.replace(ISO_DATE, (iso) => lastVisitDayLabel(iso, referenceYear) || iso);

// A date column: "Sep 14", then the year on its own line when it isn't the visit's.
const dateLines = (iso?: string | null, referenceYear?: number): string[] => {
  const d = parseDay(iso);
  if (!d) return [];
  const lines = [d.toLocaleDateString("en-US", { month: "short", day: "numeric" })];
  if (referenceYear != null && d.getFullYear() !== referenceYear) lines.push(String(d.getFullYear()));
  return lines;
};

// A sentence ends at . ! or ? before a space or the end of the text, except
// after the abbreviations a clinical note uses mid-sentence.
const ABBREVIATION = /(?:^|[\s(])(?:Dr|Mr|Mrs|e\.g)\.$/i;

// "One. Two. Three." → ["One.", "Two. Three."]; one sentence → [text, ""].
export const splitFirstSentence = (text: string): [string, string] => {
  const end = /[.!?](?=\s|$)/g;
  for (let m = end.exec(text); m; m = end.exec(text)) {
    const head = text.slice(0, m.index + 1);
    if (!ABBREVIATION.test(head)) return [head.trim(), text.slice(m.index + 1).trim()];
  }
  return [text.trim(), ""];
};

const withLabel = (sources?: BriefSource[] | null): BriefSource[] => (sources ?? []).filter((s) => !!s?.label?.trim());

// A ref as one token of a space-separated data attribute.
const refToken = (ref: string): string => ref.replace(/[\s%]/g, (c) => encodeURIComponent(c));

const AI_MARK_TITLE = "Model summary, checked against the chart";
// Lab panels shown before "Show N more panels".
const MAX_LAB_PANELS = 4;
// The card's own width (border box) from which it lays out in two columns,
// and under which a lab table folds its units into the result.
const WIDE_MIN_PX = 720;
const NARROW_MAX_PX = 400;
// Under this the four tiles sit two by two (same tiles, same order).
const TILE_GRID_MAX_PX = 340;
const FLASH_MS = 1500;
// The last-visit row's ref for jumps (a note citation lands there).
const LAST_VISIT_REF = "last-visit";
const TILE_LABEL: Record<TileKey, string> = {
  abnormal: "abnormal labs",
  unreviewed: "unreviewed",
  pending: "pending",
  records: "new records",
};
// On a narrow card, a lab name longer than this puts its date and tags under it.
const LAB_NAME_INLINE_CHARS = 12;
// Tile names shown before "+N" (one on a narrow card).
const TILE_NAMES = 2;

const ClipboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="3" y="2.5" width="10" height="11.5" rx="1.5" stroke="#6366F1" strokeWidth="1.3" />
    <path d="M6 2.5h4v1.5H6z" fill="#EEF2FF" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 7h5M5.5 9.5h5M5.5 12h3" stroke="#6366F1" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// Marks the model's text (the summary, the note summary, the questions) apart from the facts.
const AiMark: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <span role="img" aria-label={AI_MARK_TITLE} title={AI_MARK_TITLE} style={{ ...styles.aiMark, ...style }}>
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" style={{ display: "block" }}>
      <path
        d="M6 .8c.3 2.6 1.6 3.9 4.2 4.2C7.6 5.3 6.3 6.6 6 9.2 5.7 6.6 4.4 5.3 1.8 5 4.4 4.7 5.7 3.4 6 .8z"
        fill="currentColor"
      />
      <path
        d="M9.7 8.3c.1.8.4 1.1 1.2 1.2-.8.1-1.1.4-1.2 1.2-.1-.8-.4-1.1-1.2-1.2.8-.1 1.1-.4 1.2-1.2z"
        fill="currentColor"
      />
    </svg>
  </span>
);

// The two glyphs the card keeps: a warning beside an identity line and the
// speech bubble on a question (stroke = currentColor). Rows carry no type
// glyph: the line under a title says what it is.
type GlyphName = "warning" | "ask";

const GLYPHS: Record<GlyphName, React.ReactNode> = {
  warning: (
    <>
      <path
        d="M6.5 2.3a.6.6 0 0 1 1 0l5.3 9.4a.6.6 0 0 1-.5.9H1.7a.6.6 0 0 1-.5-.9z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M7 5.8v2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="7" cy="10.6" r=".7" fill="currentColor" />
    </>
  ),
  ask: (
    <path
      d="M2 3.5A1.5 1.5 0 0 1 3.5 2h7A1.5 1.5 0 0 1 12 3.5v5A1.5 1.5 0 0 1 10.5 10H6l-3 2.5V10A1.5 1.5 0 0 1 2 8.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
};

const GLYPH_COLOR: Record<GlyphName, string> = { warning: "#B45309", ask: "#6366F1" };

const Glyph: React.FC<{ name: GlyphName }> = ({ name }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
    focusable="false"
    style={{ ...styles.glyph, color: GLYPH_COLOR[name] }}
  >
    {GLYPHS[name]}
  </svg>
);

// The disclosure caret at a row's leading edge: "›", turned down when open.
// It only says the row unfolds; the row's head is what you tap.
const Caret: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className="pvb-caret"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
    focusable="false"
    style={{ ...styles.caret, ...(open ? styles.caretOpen : {}) }}
  >
    <path d="M5.2 3.5 8.7 7l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// "↗": opens in a new tab.
const NewTabArrow = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" aria-hidden="true" focusable="false" style={{ display: "block" }}>
    <path
      d="M2.5 6.5 6.5 2.5M3.3 2.5h3.2v3.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const MEDIA_LABEL: Record<string, string> = { pdf: "PDF", fax: "Fax", image: "Image", note: "Note" };

// What a chip opens, for its tooltip and screen readers: "PDF, 4 pages".
const mediaWords = (media: string, pages?: number | null): string => {
  const label = MEDIA_LABEL[media] ?? "File";
  return pages && media !== "image" ? `${label}, ${pages} ${pages === 1 ? "page" : "pages"}` : label;
};

// The noun chip at a row's right edge: names the file it opens in a new tab.
// Outlined and grey (blue only on hover), so blue keeps meaning "jump".
const OriginalChip: React.FC<{ media: string; label: string; onOpen: () => void }> = ({ media, label, onOpen }) => (
  <button
    type="button"
    className="pvb-chip"
    data-original={media}
    aria-label={`${label} · opens in a new tab`}
    title={`${label} · opens in a new tab`}
    style={styles.originalChip}
    onClick={(e) => {
      e.stopPropagation();
      onOpen();
    }}
  >
    {MEDIA_LABEL[media] ?? "File"}
    <NewTabArrow />
  </button>
);

// Hover and keyboard focus, which inline styles can't express. Scoped to the
// card's own class names.
const CARD_CSS = `
.pvb-head { border-radius: 8px; transition: background-color 120ms; }
.pvb-head[data-can-open="true"] { cursor: pointer; }
.pvb-head[data-can-open="true"]:hover { background-color: #F6F7F9; }
.pvb-head[data-can-open="true"]:hover .pvb-caret { color: #4B5563; }
.pvb-toggle:focus-visible, .pvb-chip:focus-visible, .pvb-jump:focus-visible, .pvb-more:focus-visible {
  outline: 2px solid #6366F1; outline-offset: 2px; border-radius: 4px;
}
.pvb-chip { position: relative; }
.pvb-chip::before { content: ""; position: absolute; inset: -6px -4px; }
.pvb-chip:hover { color: #2563EB; border-color: #93C5FD; background-color: #F8FBFF; }
.pvb-more:hover { color: #374151; }
.pvb-jump:hover { text-decoration-color: #2563EB; }
.pvb-show-more:hover { background-color: #E0E7FF; border-color: #A5B4FC; }
.pvb-show-more:focus-visible { outline: 2px solid #6366F1; outline-offset: 2px; }
.pvb-show-more .pvb-caret { color: inherit !important; }
`;

// "Show more": a brief opens collapsed (tiles clear, the rest blurred) and the
// tap that opens it is the signal the clinician wanted it. An opened brief stays
// open on this device, so a remount neither hides it again nor counts it twice.
const OPENED_KEY = "prechart-brief-opened";
const OPENED_MAX = 300;
const readOpened = (): string[] => {
  try {
    const ids = JSON.parse(window.localStorage.getItem(OPENED_KEY) || "[]");
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
};
const wasOpened = (briefId?: string | null) => !!briefId && readOpened().includes(briefId);
const rememberOpened = (briefId: string) => {
  try {
    const ids = [briefId, ...readOpened().filter((id) => id !== briefId)].slice(0, OPENED_MAX);
    window.localStorage.setItem(OPENED_KEY, JSON.stringify(ids));
  } catch {
    // Private mode / full storage: the brief just opens collapsed next time.
  }
};
// Seen = this much of the card on screen for VIEW_DWELL_MS.
const VIEW_MIN_PX = 160;
const VIEW_DWELL_MS = 1000;
// The observer only calls back when visibility crosses a threshold, so the
// 160px check is only as fine as these steps (2% of the card's height).
const VIEW_THRESHOLDS = Array.from({ length: 51 }, (_, i) => i / 50);

const Tag: React.FC<{ tone: React.CSSProperties; children: React.ReactNode }> = ({ tone, children }) => (
  <span style={{ ...styles.tag, ...tone }}>{children}</span>
);

// A date column.
const DateCell: React.FC<{ lines: string[]; style?: React.CSSProperties }> = ({ lines, style }) => (
  <span style={{ ...styles.dayDate, ...style }}>
    {lines.map((line, i) => (
      <span key={i} style={i ? styles.dayYear : { display: "block" }}>
        {line}
      </span>
    ))}
  </span>
);

// The card's own width, measured, so it lays out for wherever it is mounted
// (the Superdoc panel, a wide view). 0 until measured or without ResizeObserver.
const useCardWidth = (el: HTMLElement | null): number => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      // Border box: the card's padding changes with the layout; its width doesn't.
      setWidth(entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return width;
};

// ── Lab chips: flagged values on one line ──

type Chip = { label: string; value: string; flag: string };
type Trailing = { text: string; tone: "red" | "grey" } | null;

// The flagged values as chips, then what the line can't show: "+2 abnormal"
// when a flagged value didn't fit, else "3 not flagged" (dropped before any
// flagged value when the line is full). A panel with nothing flagged says so
// in one chip. Never a second line.
// `normal` counts values the EHR left unflagged; null: unknown (an older, cut
// brief), so the line offers "More values". The card says "not flagged", never
// "normal": the EHR's flag is all it knows.
// `flaggedTotal` null: more flagged values may exist than arrived ("+N or more abnormal").
const ChipLine: React.FC<{ chips: Chip[]; flaggedTotal: number | null; normal: number | null; lead?: Trailing }> = ({
  chips,
  flaggedTotal,
  normal,
  lead,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState<number | null>(null);
  // Every flagged chip fits but the "N not flagged" count doesn't: the count goes.
  const [countFits, setCountFits] = useState(true);
  const signature = chips.map((c) => `${c.label}${c.value}${c.flag}`).join("|");

  // biome-ignore lint/correctness/useExhaustiveDependencies: the chips' text is the dependency.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const fit = () => {
      const width = row.clientWidth;
      const kids = Array.from(measure.children) as HTMLElement[];
      // Unmeasurable (no layout): show them all.
      if (!width || !kids.length || !kids[0].offsetWidth) {
        setShown(chips.length);
        setCountFits(true);
        return;
      }
      const gap = 5;
      const widths = kids.slice(0, chips.length).map((k) => k.offsetWidth);
      const moreWidth = kids[chips.length]?.offsetWidth ?? 0;
      const capped = flaggedTotal == null || flaggedTotal > chips.length;
      const normalWidth = kids[chips.length + 1]?.offsetWidth ?? 0;
      const span = (k: number, tail: number) => widths.slice(0, k).reduce((n, w) => n + w + gap, 0) + (tail || -gap);
      let fits = 1;
      let withCount = true;
      for (let k = chips.length; k >= 1; k--) {
        const tail = k < chips.length || capped ? moreWidth : normal == null || normal > 0 ? normalWidth : 0;
        if (span(k, tail) <= width) {
          fits = k;
          break;
        }
        // All flagged values fit without the count: keep them, drop the count.
        if (k === chips.length && !capped && span(k, 0) <= width) {
          fits = k;
          withCount = false;
          break;
        }
      }
      setShown(fits);
      setCountFits(withCount);
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    return () => observer.disconnect();
  }, [signature, normal, flaggedTotal]);

  const count = shown ?? chips.length;
  // Flagged values past the cap count too; an unknown total is a lower bound.
  const hiddenFlagged = Math.max(chips.length, flaggedTotal ?? 0) - count;
  const orMore = flaggedTotal == null ? " or more" : "";
  const trailing: Trailing =
    hiddenFlagged > 0 || orMore
      ? { text: hiddenFlagged > 0 ? `+${hiddenFlagged}${orMore} abnormal` : "More abnormal values", tone: "red" }
      : !countFits
        ? null
        : normal == null
          ? { text: "More values", tone: "grey" }
          : normal > 0
            ? { text: `${normal} not flagged`, tone: "grey" }
            : null;
  const renderChip = (chip: Chip, i: number, measuring = false) => (
    <span key={i} style={{ ...styles.chip, ...styles.chipFlagged, ...(measuring ? {} : styles.chipFit) }}>
      {chip.label && <span style={styles.chipName}>{chip.label} </span>}
      <b style={styles.chipValue}>{chip.value}</b>
      {chip.flag && <span style={styles.flagBadge}>{chip.flag}</span>}
    </span>
  );
  const renderTrailing = (t: NonNullable<Trailing>, key: string) => (
    <span key={key} style={{ ...styles.chip, ...(t.tone === "red" ? styles.chipMore : styles.chipNormal) }}>
      {t.text}
    </span>
  );

  if (lead) {
    return <div style={styles.chipRow}>{renderTrailing(lead, "lead")}</div>;
  }
  return (
    <div style={styles.chipLineWrap}>
      <div ref={rowRef} style={{ ...styles.chipRow, visibility: shown == null ? "hidden" : "visible" }}>
        {chips.slice(0, count).map((chip, i) => renderChip(chip, i))}
        {trailing && renderTrailing(trailing, "trailing")}
      </div>
      {/* Every chip at its natural width, for the fit; never seen. */}
      <div ref={measureRef} style={styles.measure} aria-hidden="true">
        {chips.map((chip, i) => renderChip(chip, i, true))}
        {renderTrailing(
          { text: `+${Math.max(chips.length, flaggedTotal ?? 0)}${orMore} abnormal`, tone: "red" },
          "m-more",
        )}
        {renderTrailing({ text: normal == null ? "More values" : `${normal} not flagged`, tone: "grey" }, "m-normal")}
      </div>
    </div>
  );
};

// The value with its units, so a chip reads on its own ("5.9 mmol/L", "7.9%").
const valueText = (v: BriefLabValue): string =>
  `${v.value}${v.units === "%" ? "%" : v.units?.trim() ? ` ${v.units.trim()}` : ""}`;

type FeedbackState = { phase: "idle" | "sending" | "sent" | "error"; helpful?: boolean };

const PreVisitBrief: React.FC<PreVisitBriefProps> = ({ data, clientId, frozen, onAsk, askDisabled }) => {
  const idPrefix = useId();
  const [card, setCard] = useState<HTMLElement | null>(null);
  const cardWidth = useCardWidth(card);
  const wide = cardWidth >= WIDE_MIN_PX;
  const narrow = cardWidth > 0 && cardWidth < NARROW_MAX_PX;
  const tilesTwoByTwo = cardWidth > 0 && cardWidth < TILE_GRID_MAX_PX;
  const [filter, setFilter] = useState<TileKey | null>(null);
  const [openLabs, setOpenLabs] = useState<Set<string>>(() => new Set());
  const [allLabs, setAllLabs] = useState(false);
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set());
  const [allPending, setAllPending] = useState(false);
  const [openDays, setOpenDays] = useState<Set<string>>(() => new Set());
  const [earlierOpen, setEarlierOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  // A summary phrase's fact, scrolled to and highlighted for FLASH_MS.
  const [flash, setFlash] = useState<string | null>(null);
  const [scrollTo, setScrollTo] = useState<string | null>(null);
  // A capped panel's every value, fetched when its table opens.
  const [fullValues, setFullValues] = useState<Record<string, BriefLabValue[] | "loading" | "failed">>({});
  const [refresh, setRefresh] = useState<"idle" | "sending" | PrechartRefreshResult>("idle");
  const [feedback, setFeedback] = useState<FeedbackState>({ phase: "idle" });
  const [expanded, setExpanded] = useState(() => wasOpened(data?.brief_id));
  const viewedAt = useRef<number | null>(null);
  const viewedId = useRef<string | null>(null);

  // Analytics: the card was actually on screen (once per brief per mount; the
  // effect reruns when a newer brief freezes this one, which isn't a new view).
  useEffect(() => {
    const id = data?.brief_id;
    if (!card || !id || viewedId.current === id || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible =
          entry.isIntersecting &&
          entry.intersectionRect.height >= Math.min(VIEW_MIN_PX, entry.boundingClientRect.height);
        if (!visible) {
          clearTimeout(timer);
          timer = undefined;
          return;
        }
        if (timer) return;
        timer = setTimeout(() => {
          viewedId.current = id;
          viewedAt.current = Date.now();
          trackEvent(EventType.SUPERDOC_PREVISIT_BRIEF_VIEWED, {
            brief_id: id,
            frozen: !!frozen,
            collapsed: !wasOpened(id),
          });
          observer.disconnect();
        }, VIEW_DWELL_MS);
      },
      { threshold: VIEW_THRESHOLDS },
    );
    observer.observe(card);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [card, data?.brief_id, frozen]);

  useEffect(() => {
    if (!scrollTo || !card) return;
    const target = card.querySelector(`[data-refs~="${refToken(scrollTo).replace(/["\\]/g, "\\$&")}"]`);
    if (target && typeof (target as HTMLElement).scrollIntoView === "function") {
      (target as HTMLElement).scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    setScrollTo(null);
  }, [scrollTo, card]);

  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flash]);

  // An open in-house panel the brief cut at 25 values loads the rest.
  useEffect(() => {
    const briefId = data?.brief_id;
    if (!briefId) return;
    for (const lab of data.labs ?? []) {
      if (!lab?.ref || lab.outside || !lab.truncated || !openLabs.has(lab.ref) || fullValues[lab.ref]) continue;
      const ref = lab.ref;
      setFullValues((prev) => ({ ...prev, [ref]: "loading" }));
      fetchPrechartLabValues(prechartBaseUrl(), briefId, ref, clientId).then((res) =>
        setFullValues((prev) => ({ ...prev, [ref]: res?.values?.length ? res.values : "failed" })),
      );
    }
  }, [data, openLabs, fullValues, clientId]);

  // Analytics: the card was shown (once per brief), with what it held —
  // counts and flags only, never patient data.
  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    const id = data?.brief_id;
    if (!id || shownFor.current === id) return;
    shownFor.current = id;
    const tileCounts = Object.fromEntries(
      buildTiles((data.events ?? []).filter((e) => !!e?.title?.trim())).map((t) => [`tile_${t.key}`, t.count]),
    );
    trackEvent(EventType.SUPERDOC_PREVISIT_BRIEF_SHOWN, {
      brief_id: id,
      frozen: !!frozen,
      composer: data.composer ?? null,
      has_summary: !!data.summary?.segments?.length,
      lab_panels: data.labs?.length ?? 0,
      events: data.events?.length ?? 0,
      questions: data.questions?.length ?? 0,
      has_anchor: !!data.anchor,
      coverage_complete: data.coverage?.complete !== false,
      collapsed: !wasOpened(id),
      ...tileCounts,
    });
  }, [data, frozen]);

  if (!data?.brief_id) return null;

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const appt = data.appointment;
  const anchor = data.anchor;
  // Analytics: a tap on the card — which control, with the card's state.
  const layout = wide ? "wide" : narrow ? "narrow" : "single";
  const track = (action: string, props: Record<string, unknown> = {}) =>
    trackEvent(EventType.SUPERDOC_PREVISIT_BRIEF_ACTION, {
      action,
      brief_id: data.brief_id,
      frozen: !!frozen,
      layout,
      filter: filter ?? null,
      ...props,
    });
  const expand = (source: "button" | "tile", props: Record<string, unknown> = {}) => {
    track("expand", {
      source,
      ms_since_viewed: viewedAt.current ? Date.now() - viewedAt.current : null,
      ...props,
    });
    rememberOpened(data.brief_id);
    setExpanded(true);
  };
  const referenceYear = parseDay(appt?.date)?.getFullYear();
  const pretty = (text?: string | null) => prettyDates(text ?? "", referenceYear);
  const dayLabel = (iso?: string | null) => lastVisitDayLabel(iso, referenceYear);
  const yourVisit = anchor?.scope === "provider";
  const aiComposed = data.composer !== "deterministic";
  const lastVisit = data.last_visit;
  const anchorDay = dayKey(anchor?.date || lastVisit?.date);

  // Every event with a title, in payload order (newest first).
  const rows: BriefEvent[] = (data.events ?? []).filter((event) => !!event?.title?.trim());
  const labsKnown = Array.isArray(data.labs);
  const labs = (data.labs ?? []).filter((lab) => !!lab?.ref);
  const labRefs = new Set(labs.map((lab) => lab.ref));
  const tiles = buildTiles(rows);
  const tileOf = (key: TileKey) => tiles.find((t) => t.key === key) as Tile;
  const activeFilter = filter && tileOf(filter).count > 0 ? filter : null;

  // ── Header ──
  const apptLine = appt ? [appointmentDayLabel(appt.date), appt.time, appt.visit_type].filter(Boolean).join(" · ") : "";
  const windowDays = anchor?.window_days ?? 0;
  const windowNote = !anchor?.window_capped
    ? ""
    : windowDays > 0
      ? `showing the last ${windowDays} days`
      : "showing a shorter window";
  // The anchor is this clinician's own visits, so "new" means new to them,
  // not to the practice.
  const sinceLine = data.is_new_patient ? (
    <span style={styles.meta}>No earlier visit with you on record</span>
  ) : anchor?.date ? (
    <span style={styles.meta}>
      {yourVisit ? "Since your last visit" : "Since the last visit"} ·{" "}
      <b style={styles.metaDate}>{dayLabel(anchor.date)}</b>
      {[daysAgoLabel(anchor.days_ago), anchor.note_signed === false ? "note not signed" : "", windowNote]
        .filter(Boolean)
        .map((part) => ` · ${part}`)
        .join("")}
    </span>
  ) : null;

  // The prior note's sources (the note itself) and the last visit's row.
  const noteSources = withLabel(lastVisit?.sources).filter((src) => src.kind === "note" || src.ref.startsWith("note:"));
  const noteRefs = new Set(noteSources.map((src) => src.ref));

  // ── Jumping to a fact (a summary phrase) ──
  const eventFor = (ref: string): BriefEvent | undefined =>
    rows.find((e) => e.ref === ref) ?? rows.find((e) => (e.sources ?? []).some((s) => s.ref === ref));

  // A lab panel: show it (past the first four if need be), open its table, highlight it.
  type JumpTarget = "lab" | "note" | "row" | "none";
  const jumpToLab = (ref: string): JumpTarget => {
    const lab = labs.find((l) => l.ref === ref);
    if (!lab) return "none";
    if (labs.indexOf(lab) >= MAX_LAB_PANELS) setAllLabs(true);
    if (lab.values?.length || lab.text) setOpenLabs((prev) => new Set(prev).add(lab.ref));
    setFlash(lab.ref);
    setScrollTo(lab.ref);
    return "lab";
  };

  const jumpTo = (sources: BriefSource[]): JumpTarget => {
    const refs = sources.map((s) => s.ref);
    const labRef = refs.find((ref) => labRefs.has(ref));
    if (labRef) return jumpToLab(labRef);
    // The prior note lives on the last-visit row: open it there.
    if (lastVisit && refs.some((ref) => noteRefs.has(ref))) {
      if (activeFilter) setFilter(null);
      setNoteOpen(true);
      setFlash(LAST_VISIT_REF);
      setScrollTo(LAST_VISIT_REF);
      return "note";
    }
    const event = refs.map(eventFor).find(Boolean);
    if (!event) return "none";
    // A cited order is answered by its result's panel.
    if (labRefs.has(event.ref)) return jumpToLab(event.ref);
    // The filter stays unless it hides the row.
    const nextFilter = activeFilter && !tilesOf(event).has(activeFilter) ? null : activeFilter;
    if (nextFilter !== activeFilter) setFilter(null);
    if (event.kind === "pending") setAllPending(true);
    else {
      const day = dayKey(event.date);
      const days = new Set(openDays).add(day);
      setOpenDays(days);
      const next = buildTimeline(rows, { anchorDay, labRefs, filter: nextFilter, allPending: true, openDays: days });
      const holds = (d: Day) =>
        d.items.some((item) =>
          item.type === "row" ? item.event.ref === event.ref : item.events.some((e) => e.ref === event.ref),
        );
      if (!next.shown.some(holds)) setEarlierOpen(true);
      // It lands open: the gist, the members or the whole text, one tap.
      const [head, sub] = rowText(event);
      if (rowUnfold(event, `${head} ${sub}`)) setOpenRows((prev) => new Set(prev).add(event.ref));
    }
    setFlash(event.ref);
    setScrollTo(event.ref);
    return "row";
  };
  const tapPhrase = (sources: BriefSource[], warn: boolean) =>
    track("summary_phrase", { target: jumpTo(sources), warn });

  // ── Tiles ──
  const tileTone = (tile: Tile): React.CSSProperties =>
    tile.count === 0
      ? styles.tileZero
      : tile.key === "abnormal"
        ? styles.tileRed
        : tile.key === "unreviewed"
          ? styles.tileAmber
          : styles.tileNeutral;
  const tileCount = (n: number) => (n > 99 ? "99+" : String(n));
  const tileStrip = (
    <div
      style={{ ...styles.tiles, ...(tilesTwoByTwo ? styles.tilesTwoByTwo : {}) }}
      role="group"
      aria-label="Since the last visit"
    >
      {tiles.map((tile) => {
        const selected = activeFilter === tile.key;
        const names = tile.names.slice(0, narrow && !tilesTwoByTwo ? 1 : TILE_NAMES);
        const more = tile.names.length - names.length;
        return (
          <button
            key={tile.key}
            type="button"
            data-tile={tile.key}
            aria-pressed={selected}
            disabled={tile.count === 0}
            onClick={() => {
              if (!expanded) expand("tile", { tile: tile.key });
              track(selected ? "tile_clear" : "tile_select", { tile: tile.key, count: tile.count, via: "tile" });
              setFilter(selected ? null : tile.key);
            }}
            style={{
              ...styles.tile,
              ...(narrow ? styles.tileNarrow : {}),
              ...tileTone(tile),
              ...(selected ? styles.tileSelected : {}),
            }}
          >
            <span style={styles.tileCount}>{tileCount(tile.count)}</span>
            <span style={{ ...styles.tileLabel, ...(narrow ? styles.tileLabelNarrow : {}) }}>
              {TILE_LABEL[tile.key]}
            </span>
            <span style={styles.tileSub}>
              {tile.count === 0 ? (
                "—"
              ) : (
                <>
                  <span style={styles.tileNames}>{names.join(" · ")}</span>
                  {more > 0 && <span style={styles.tileMore}> +{more}</span>}
                </>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
  const filterBar = activeFilter ? (
    <div style={styles.filterBar} role="status">
      <span style={styles.filterLead}>
        {narrow ? "" : "Showing "}
        {tileNoun(activeFilter, tileOf(activeFilter).count)}
      </span>
      <button
        type="button"
        className="pvb-more"
        style={styles.filterClear}
        onClick={() => {
          track("tile_clear", { tile: activeFilter, via: "show_everything" });
          setFilter(null);
        }}
      >
        <span aria-hidden="true" style={styles.filterX}>
          ×
        </span>
        Show everything
      </button>
    </div>
  ) : null;

  // ── Summary ──
  const segments = (data.summary?.segments ?? []).filter((seg) => !!seg?.text);
  const written = segments.length > 0;
  const renderSegment = (seg: BriefSummarySegment, i: number) => {
    const text = pretty(seg.text);
    const sources = withLabel(seg.sources);
    if (!seg.attention) return text;
    const tone = seg.warn ? styles.phraseWarn : styles.phrase;
    if (!sources.length) {
      return (
        <b key={i} style={tone}>
          {text}
        </b>
      );
    }
    // Inline, not a <button>, so a long phrase wraps with the sentence.
    return (
      <b
        key={i}
        role="button"
        tabIndex={0}
        style={{ ...tone, ...styles.phraseButton }}
        onClick={() => tapPhrase(sources, !!seg.warn)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            tapPhrase(sources, !!seg.warn);
          }
        }}
      >
        {text}
      </b>
    );
  };
  // A document that may be another patient's must be read: one quiet line
  // under the summary, unless a warn phrase already names it.
  const warnRefs = new Set(
    segments.filter((seg) => seg.warn).flatMap((seg) => (seg.sources ?? []).map((src) => src.ref)),
  );
  const identityLines = rows
    .filter((event) => event.identity_mismatch && !warnRefs.has(event.ref))
    .map((event) => {
      const day = dayLabel(dayKey(event.date));
      return {
        ref: event.ref,
        text: `${rowName(event)}${day ? ` (${day})` : ""} may belong to another patient — check before relying on it.`,
      };
    });
  const headline = pretty(data.headline).trim();
  const summarySection =
    written || headline ? (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          Summary
          {!aiComposed && <span style={styles.plainNote}>Plain listing (AI unavailable)</span>}
        </div>
        <p style={styles.summary}>{written ? segments.map(renderSegment) : headline}</p>
        {identityLines.length > 0 && (
          <ul style={styles.identityList}>
            {identityLines.map(({ ref, text }) => (
              <li key={ref} style={styles.identityLine}>
                <Glyph name="warning" />
                <span style={styles.rowText}>{text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    ) : null;

  // ── Rows: the head unfolds the row; a chip opens its original ──

  // A tap on a head toggles it, unless it finished selecting text there.
  const headClick = (e: React.MouseEvent<HTMLElement>, toggle: () => void) => {
    const selection = typeof window.getSelection === "function" ? window.getSelection() : null;
    if (selection && !selection.isCollapsed && e.currentTarget.contains(selection.anchorNode)) return;
    toggle();
  };
  // The head's title is its keyboard control (the caret is decoration).
  const toggleProps = (canOpen: boolean, open: boolean, controls: string, toggle: () => void) =>
    canOpen
      ? {
          className: "pvb-toggle",
          role: "button",
          tabIndex: 0,
          "aria-expanded": open,
          "aria-controls": controls,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle();
            }
          },
        }
      : {};

  // A neutral name for the opened tab: what kind of document, never its text.
  const openOriginal = (
    ref: string,
    media: string,
    from: "row" | "member" | "lab" | "note",
    title: string,
    checkPatient = false,
  ) => {
    const result = openPrechartOriginal(prechartBaseUrl(), data.brief_id, ref, { title, checkPatient, clientId });
    track("original_open", { media, from, check_patient: checkPatient, blocked: result === "blocked" });
  };
  const originalChip = (
    ref: string,
    original: BriefOriginal,
    from: "row" | "member" | "lab",
    kind: string | null | undefined,
    checkPatient = false,
  ) => (
    <span style={styles.chipSlot}>
      <OriginalChip
        media={original.media}
        label={[kind, mediaWords(original.media, original.pages)].filter(Boolean).join(" · ")}
        onOpen={() => openOriginal(ref, original.media, from, kind || "Document", checkPatient)}
      />
    </span>
  );
  // "PDF · 4 pages": the first words of a document's line.
  const mediaLine = (original?: BriefOriginal | null): string =>
    original
      ? [
          MEDIA_LABEL[original.media] ?? "File",
          original.pages && original.media !== "image"
            ? `${original.pages} ${original.pages === 1 ? "page" : "pages"}`
            : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  // ── Labs ──
  // A selected tile focuses the card on its items: abnormal and unreviewed
  // keep their lab panels here; pending and new records live in the timeline.
  const labFocus = activeFilter === "abnormal" || activeFilter === "unreviewed";
  const labMatches = (lab: BriefLab): boolean => {
    if (activeFilter === "abnormal") return (lab.values ?? []).some((v) => !!v.flag) || !!lab.marked_abnormal;
    if (activeFilter === "unreviewed") return !!lab.unreviewed;
    return true;
  };
  // A panel with its every value once they've loaded.
  const withFullValues = (lab: BriefLab): BriefLab => {
    const loaded = fullValues[lab.ref];
    if (!Array.isArray(loaded)) return lab;
    return {
      ...lab,
      values: loaded,
      value_total: loaded.length,
      flagged_total: loaded.filter((v) => !!v.flag).length,
      unflagged_total: loaded.filter((v) => !v.flag && !!v.value).length,
      truncated: false,
    };
  };
  const renderLab = (briefLab: BriefLab, index: number) => {
    const lab = withFullValues(briefLab);
    const loading = fullValues[lab.ref] === "loading";
    const values = lab.values ?? [];
    const flagged = values.filter((v) => !!v.flag);
    // Totals are null for an older brief cut at 25 values: what's here is a floor.
    const totalKnown = lab.value_total != null;
    const total = Math.max(lab.value_total ?? values.length, values.length);
    // Values arrive flagged first, so a kept unflagged value means every flagged one arrived.
    const flaggedKnown = lab.flagged_total != null || !lab.truncated || flagged.length < values.length;
    const flaggedTotal = flaggedKnown ? Math.max(lab.flagged_total ?? flagged.length, flagged.length) : null;
    // A count over a partial list would be wrong: unknown unless every value is here.
    const complete = totalKnown && total === values.length;
    const normal =
      lab.unflagged_total !== undefined
        ? lab.unflagged_total
        : complete
          ? values.filter((v) => !v.flag && !!v.value).length
          : null;
    const open = openLabs.has(lab.ref);
    const hasTable = values.length > 0;
    const hasText = !hasTable && !!lab.text?.trim();
    const canOpen = hasTable || hasText;
    // A one-value panel drops the value's name only when it is the panel's own
    // ("HbA1c" in HbA1c), never when it says which test it is (K in a CMP).
    const sameName = (v: BriefLabValue) =>
      [v.short, v.name].some(
        (n) => !!n && [lab.short, lab.name].some((m) => !!m && m.toLowerCase() === n.toLowerCase()),
      );
    const chips: Chip[] = flagged.map((v) => ({
      label: total === 1 && sameName(v) ? "" : v.short || v.name,
      value: v.narrative ? "report" : valueText(v),
      flag: v.flag ?? "",
    }));
    const lead: Trailing =
      lab.read === false
        ? { text: "Values not read", tone: "grey" }
        : hasText
          ? { text: "Report", tone: "grey" }
          : !flagged.length && lab.marked_abnormal
            ? { text: "Marked abnormal", tone: "red" }
            : !flagged.length
              ? {
                  // "No values" only when the whole panel is known to be blank.
                  text:
                    values.some((v) => !!v.value) || (normal ?? 0) > 0
                      ? "None flagged"
                      : complete
                        ? "No values"
                        : "More values",
                  tone: "grey",
                }
              : null;
    const tableId = `${idPrefix}-lab-${lab.ref}`;
    const toggleLab = () => {
      track(open ? "lab_close" : "lab_open", {
        kind: hasText ? "report" : "table",
        outside: !!lab.outside,
        flagged: flaggedTotal,
        values: values.length,
        position: index,
      });
      toggleIn(setOpenLabs, lab.ref);
      if (open) setScrollTo(lab.ref);
    };
    const labMeta = (
      <>
        {/* No collection date: when it arrived, so a panel is never undated. */}
        {(lab.date || lab.arrived) && (
          <span style={styles.labDate}>{lab.date ? dayLabel(lab.date) : `Arrived ${dayLabel(lab.arrived)}`}</span>
        )}
        {lab.outside && <Tag tone={styles.tagTeal}>Prisma</Tag>}
        {lab.unreviewed && <Tag tone={styles.tagUnreviewed}>Unreviewed</Tag>}
      </>
    );
    const flashing = flash === lab.ref;
    // A narrow card gives a long name the whole line; a short one keeps its date beside it.
    const metaBelow = narrow && (lab.short || lab.name).length > LAB_NAME_INLINE_CHARS;
    const footer = [
      lab.name,
      lab.date
        ? `${lab.outside ? "Collected" : "Resulted"} ${dayLabel(lab.date)}`
        : lab.arrived
          ? `Arrived ${dayLabel(lab.arrived)}`
          : "",
      lab.outside ? "Prisma" : "",
      loading
        ? totalKnown
          ? `${values.length} of ${total} · loading the rest…`
          : `First ${values.length} values · loading the rest…`
        : !totalKnown && lab.truncated
          ? `First ${values.length} values shown`
          : total > values.length
            ? `${values.length} of ${total} values shown`
            : "",
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <li
        key={lab.ref}
        data-refs={refToken(lab.ref)}
        style={{
          ...styles.lab,
          ...(index ? styles.labNext : {}),
          ...(flashing ? styles.flash : {}),
        }}
      >
        <div
          className="pvb-head"
          data-can-open={canOpen}
          style={{ ...styles.rowHeadBox, ...(open ? styles.labHeadSticky : {}) }}
          onClick={canOpen ? (e) => headClick(e, toggleLab) : undefined}
        >
          <span style={styles.caretSlot}>{canOpen && <Caret open={open} />}</span>
          <div style={styles.labHead}>
            <span {...toggleProps(canOpen, open, tableId, toggleLab)} style={styles.labName} title={lab.name}>
              {lab.short || lab.name}
            </span>
            {!metaBelow && labMeta}
            {lab.report?.ref && (
              <span style={styles.labChip}>
                <OriginalChip
                  media={lab.report.media || "pdf"}
                  label={[
                    lab.report.title || "Lab report",
                    mediaWords(lab.report.media || "pdf", lab.report.pages),
                  ].join(" · ")}
                  onOpen={() =>
                    openOriginal(lab.report?.ref as string, lab.report?.media || "pdf", "lab", "Lab report")
                  }
                />
              </span>
            )}
          </div>
        </div>
        {/* A long name on a narrow card takes the whole line; date and tags go under it. */}
        {metaBelow && (lab.date || lab.arrived || lab.outside || lab.unreviewed) && (
          <div style={styles.labMetaLine}>{labMeta}</div>
        )}
        {open && hasTable ? (
          <div id={tableId} style={styles.tableBox}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Test</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Result</th>
                  {!narrow && <th style={styles.th}>Units</th>}
                  <th style={styles.th}>Range</th>
                </tr>
              </thead>
              <tbody>
                {values.map((v, i) =>
                  v.narrative ? (
                    // A report reads as text across the row, its flag beside the name.
                    <tr key={i} style={v.flag ? styles.trFlagged : undefined}>
                      <td style={styles.td}>
                        {v.short || v.name}
                        {v.flag && <span style={styles.flagBadge}>{v.flag}</span>}
                      </td>
                      <td colSpan={narrow ? 2 : 3} style={{ ...styles.td, ...styles.tdReport }}>
                        {v.value}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} style={v.flag ? styles.trFlagged : undefined}>
                      <td style={styles.td}>{v.short || v.name}</td>
                      <td style={{ ...styles.td, ...styles.tdValue, ...(v.flag ? styles.tdValueFlagged : {}) }}>
                        {v.value}
                        {narrow && v.units ? <span style={styles.tdUnitsInline}> {v.units}</span> : null}
                        {v.flag && <span style={styles.flagBadge}>{v.flag}</span>}
                      </td>
                      {!narrow && <td style={styles.td}>{v.units}</td>}
                      <td style={{ ...styles.td, ...styles.tdRange }}>{v.range}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
            {footer && <div style={styles.tableFoot}>{footer}</div>}
          </div>
        ) : open && hasText ? (
          <div id={tableId} style={styles.reportBox}>
            {pretty(lab.text)}
            {footer && <div style={styles.reportFoot}>{footer}</div>}
          </div>
        ) : (
          <ChipLine chips={chips} flaggedTotal={flaggedTotal} normal={normal} lead={lead} />
        )}
      </li>
    );
  };
  const listedLabs = activeFilter ? (labFocus ? labs.filter(labMatches) : []) : labs;
  const shownLabs = allLabs ? listedLabs : listedLabs.slice(0, MAX_LAB_PANELS);
  const moreLabs = listedLabs.length - MAX_LAB_PANELS;
  const labsSection =
    !labsKnown || (activeFilter && !listedLabs.length) ? null : (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Labs</div>
        {listedLabs.length ? (
          <>
            <ul style={styles.labs}>{shownLabs.map(renderLab)}</ul>
            {moreLabs > 0 && (
              <button
                type="button"
                className="pvb-more"
                style={styles.moreLink}
                onClick={() => {
                  track("labs_more_panels", { expanded: !allLabs, panels: listedLabs.length });
                  setAllLabs((all) => !all);
                }}
              >
                {allLabs ? "Show less" : `Show ${moreLabs} more ${moreLabs === 1 ? "panel" : "panels"}`}
              </button>
            )}
          </>
        ) : (
          <div style={styles.quietLine}>
            {anchor?.date ? `No lab results since ${dayLabel(anchor.date)}.` : "No lab results on record."}
          </div>
        )}
      </div>
    );

  // ── Timeline ──
  const timelineRows = labFocus ? rows.filter((e) => !labRefs.has(e.ref)) : rows;
  const timeline = buildTimeline(timelineRows, {
    anchorDay,
    labRefs,
    filter: activeFilter,
    allPending,
    openDays,
  });

  // Title = what it is; the line under it = where it came from and the gist.
  const rowText = (event: BriefEvent): [string, string] => {
    const title = pretty(event.title).trim();
    const sub = pretty(event.sub).trim();
    if (event.kind === "telephone") return ["Call", [title, sub].filter(Boolean).join(" · ")];
    if (event.kind === "office" || event.kind === "other_visit") {
      // The encounter's own kind ("Office visit", "Web encounter"); its title
      // repeats that when nothing else was recorded.
      const kind = rowName(event);
      return [kind, [title === kind ? "" : title, sub].filter(Boolean).join(" · ")];
    }
    // Paperwork reads by what it is; its members are one tap away.
    if (event.ref === "fold:admin") {
      const n = event.count ?? event.members?.length ?? 0;
      return [rowName(event), [n ? `${n} ${n === 1 ? "document" : "documents"}` : "", sub].filter(Boolean).join(" · ")];
    }
    if (event.kind === "fold") return [title, sub];
    const [head, ...rest] = title.split(" · ");
    const name = event.kind === "result" || event.kind === "outside_result" ? event.short || event.name || head : head;
    // An unread document says why in words of its own.
    if (event.kind === "document" && event.read === false && event.unread_line?.trim()) {
      return [name.trim(), pretty(event.unread_line).trim()];
    }
    return [name.trim(), [mediaLine(event.original), ...rest, sub].filter(Boolean).join(" · ")];
  };

  // Text already on the row, to tell whether unfolding would add anything.
  const squash = (text: string) => text.replace(/…$/, "").replace(/\s+/g, " ").trim().toLowerCase();
  const adds = (detail: string, shown: string) => !!squash(detail) && !squash(shown).includes(squash(detail));

  // What a row unfolds to: a document's gist, a fold's members, or the whole
  // text of a call, a visit or a report. Nothing: no caret.
  type Unfold =
    | { type: "members"; members: BriefFoldMember[] }
    | { type: "labels"; labels: string[] }
    | { type: "gist"; sources: BriefSource[] }
    | { type: "text"; sources: BriefSource[] };
  const rowUnfold = (event: BriefEvent, shown: string): Unfold | null => {
    if (event.kind === "pending") return null;
    if (event.kind === "fold") {
      const members = (event.members ?? []).filter((m) => !!m?.title?.trim());
      if (members.length) return { type: "members", members };
      // An older brief's fold names its members in its sources.
      const labels = withLabel(event.sources).map((src) => pretty(src.label));
      return labels.length ? { type: "labels", labels } : null;
    }
    if (event.kind === "document" && event.read === false) return null;
    const sources = withLabel(event.sources).filter(
      (src) => !!src.summary?.trim() || !!src.findings?.length || !!src.plan?.length || adds(pretty(src.detail), shown),
    );
    if (!sources.length) return null;
    return { type: event.kind === "document" ? "gist" : "text", sources };
  };

  const renderGist = (src: BriefSource) => {
    const findings = (src.findings ?? []).map((f) => pretty(f).trim()).filter(Boolean);
    const plan = (src.plan ?? []).map((f) => pretty(f).trim()).filter(Boolean);
    const summary = pretty(src.summary).trim();
    if (!summary && !findings.length && !plan.length) return <div key={src.ref}>{pretty(src.detail)}</div>;
    return (
      <div key={src.ref} style={styles.gist}>
        {summary && (
          <div>
            <AiMark />
            <b style={styles.gistLabel}>Summary</b> {summary}
          </div>
        )}
        {findings.length > 0 && (
          <div>
            <b style={styles.gistLabel}>Findings</b> {findings.join("; ")}
          </div>
        )}
        {plan.length > 0 && (
          <div>
            <b style={styles.gistLabel}>Plan</b> {plan.join("; ")}
          </div>
        )}
      </div>
    );
  };

  const renderMembers = (members: BriefFoldMember[]) => (
    <ul style={styles.members}>
      {members.map((m) => {
        // The member's day leads its line: the title stays whole beside its chip.
        const line = [
          dayLabel(dayKey(m.date)),
          ...(m.read === false && m.unread_line?.trim()
            ? [pretty(m.unread_line)]
            : [mediaLine(m.original), pretty(m.sub)]),
        ]
          .filter((x) => !!x?.trim())
          .join(" · ");
        return (
          <li key={m.ref} style={styles.member} data-refs={refToken(m.ref)}>
            {m.original && originalChip(m.ref, m.original, "member", m.doc_kind, !!m.identity_mismatch)}
            <span style={styles.memberTitle}>{pretty(m.title)}</span>
            {m.identity_mismatch && <Tag tone={{ ...styles.tagUnreviewed, ...styles.tagInline }}>Check patient</Tag>}
            {m.unreviewed && <Tag tone={{ ...styles.tagUnreviewed, ...styles.tagInline }}>Unreviewed</Tag>}
            {line && <span style={styles.memberSub}>{line}</span>}
          </li>
        );
      })}
    </ul>
  );

  const renderUnfold = (unfold: Unfold, id: string) => (
    <div id={id} style={styles.unfolded}>
      {unfold.type === "members" ? (
        renderMembers(unfold.members)
      ) : unfold.type === "labels" ? (
        <ul style={styles.members}>
          {unfold.labels.map((label, i) => (
            <li key={i} style={styles.member}>
              <span style={styles.memberTitle}>{label}</span>
            </li>
          ))}
        </ul>
      ) : unfold.type === "gist" ? (
        <div style={styles.rowDetail}>{unfold.sources.map(renderGist)}</div>
      ) : (
        <div style={styles.rowDetail}>
          {unfold.sources.map((src, i) => (
            <div key={i}>{pretty(src.detail)}</div>
          ))}
        </div>
      )}
    </div>
  );

  const toggleRow = (event: BriefEvent, open: boolean) => {
    track(open ? "row_details_close" : "row_details_open", { kind: event.kind });
    toggleIn(setOpenRows, event.ref);
  };

  const renderRow = (event: BriefEvent) => {
    const [head, sub] = rowText(event);
    const unfold = rowUnfold(event, `${head} ${sub}`);
    const canOpen = !!unfold;
    const open = canOpen && openRows.has(event.ref);
    const detailId = `${idPrefix}-row-${event.ref}`;
    const toggle = () => toggleRow(event, open);
    const refs = [event.ref, ...(event.sources ?? []).map((s) => s.ref)].map(refToken).join(" ");
    // A call or a report unfolds to its whole text, which replaces the line.
    const subReplaced = open && unfold?.type === "text";
    const tags = [
      event.identity_mismatch && (
        <Tag key="identity" tone={{ ...styles.tagUnreviewed, ...styles.tagInline }}>
          Check patient
        </Tag>
      ),
      event.unreviewed && (
        <Tag key="unreviewed" tone={{ ...styles.tagUnreviewed, ...styles.tagInline }}>
          Unreviewed
        </Tag>
      ),
      (event.kind === "other_visit" || event.other_provider) && (
        <Tag key="other" tone={{ ...styles.tagTeal, ...styles.tagInline }}>
          Another provider
        </Tag>
      ),
      (event.kind === "outside_result" || event.kind === "outside_procedure") && (
        <Tag key="prisma" tone={{ ...styles.tagTeal, ...styles.tagInline }}>
          Prisma
        </Tag>
      ),
    ].filter(Boolean);
    return (
      <li
        key={event.ref}
        data-kind={event.kind}
        data-refs={refs}
        style={{ ...styles.eventRow, ...(flash === event.ref ? styles.flash : {}) }}
      >
        <div
          className="pvb-head"
          data-can-open={canOpen}
          style={styles.rowHeadBox}
          onClick={canOpen ? (e) => headClick(e, toggle) : undefined}
        >
          <span style={styles.caretSlot}>{canOpen && <Caret open={open} />}</span>
          <div style={styles.rowBody}>
            {event.kind === "document" &&
              event.original &&
              originalChip(event.ref, event.original, "row", event.doc_kind, !!event.identity_mismatch)}
            <span
              {...toggleProps(canOpen, open, detailId, toggle)}
              style={event.kind === "fold" ? styles.rowTitleQuiet : styles.rowTitle}
            >
              {head}
            </span>
            {tags}
            {sub && !subReplaced && (
              <span style={canOpen || event.kind === "document" ? styles.rowSub : styles.rowSubWhole}>{sub}</span>
            )}
          </div>
        </div>
        {open && unfold && renderUnfold(unfold, detailId)}
      </li>
    );
  };

  // "Labs resulted · CBC, CMP": each panel name jumps to its panel in Labs.
  const renderLabLine = (events: BriefEvent[], day: string) => {
    const panels = Array.from(
      new Map(events.map((e) => [e.short || e.name || rowName(e), e.ref] as [string, string])).entries(),
    );
    const refs = events.map((e) => refToken(e.ref)).join(" ");
    return (
      <li key={`labs-${day}`} data-kind="labs" data-refs={refs} style={styles.eventRow}>
        <div style={styles.rowHeadBox}>
          <span style={styles.caretSlot} />
          <span style={styles.labLine}>
            Labs resulted ·{" "}
            {panels.map(([name, ref], i) => (
              <span key={ref}>
                {i > 0 && ", "}
                <button
                  type="button"
                  className="pvb-jump"
                  style={styles.labLineJump}
                  onClick={() =>
                    track("timeline_lab_line", { panels: panels.length, index: i, target: jumpToLab(ref) })
                  }
                >
                  {name}
                </button>
              </span>
            ))}
          </span>
        </div>
      </li>
    );
  };

  const renderItem = (item: DayItem, day: string) =>
    item.type === "labs" ? renderLabLine(item.events as BriefEvent[], day) : renderRow(item.event as BriefEvent);

  const renderDay = (group: Day, older = false) => {
    const items = group.items.slice(0, group.items.length - group.hidden);
    const dayOpen = openDays.has(group.day) && group.items.length > MAX_DAY_ROWS;
    return (
      <li key={group.day || "undated"} style={styles.day} data-day={group.day || "undated"}>
        <DateCell lines={dateLines(group.day, referenceYear)} style={older ? styles.dayDateOlder : undefined} />
        <div style={styles.dayBody}>
          <ul style={styles.dayRows}>{items.map((item) => renderItem(item, group.day))}</ul>
          {(group.hidden > 0 || dayOpen) && (
            <button
              type="button"
              className="pvb-more"
              style={{ ...styles.moreLink, ...styles.moreInRail }}
              onClick={() => {
                track("timeline_day_more", { hidden: group.hidden, expanded: !dayOpen });
                toggleIn(setOpenDays, group.day);
              }}
            >
              {dayOpen ? "Show less" : `Show ${group.hidden} more${group.day ? ` on ${dayLabel(group.day)}` : ""}`}
            </button>
          )}
        </div>
      </li>
    );
  };

  // The last visit: the timeline's last row.
  const note = lastVisit?.note;
  const noteElsewhere = !!note?.available && note.matches_anchor === false;
  const visitLabel = noteElsewhere
    ? ["Last note on file", dayLabel(note?.service_date)].filter(Boolean).join(" · ")
    : lastVisit?.mine ?? yourVisit
      ? "Your visit"
      : "Last visit";
  const visitTail = [
    noteElsewhere ? "" : lastVisit?.visit_type,
    (note?.plan_phrases ?? [])
      .map((p) => pretty(p).trim())
      .filter(Boolean)
      .join("; "),
    pretty(lastVisit?.orders_text).trim(),
  ].filter(Boolean);
  const noteSummary = pretty(note?.summary).trim();
  // The note's own words, shown with its summary: what a note citation rests on.
  const noteExcerpts = noteSources.filter((src) => !!src.detail?.trim());
  const noteMore = !!noteSummary || noteExcerpts.length > 0;
  const noteId = `${idPrefix}-note`;
  const noteRef = note?.available ? note.ref : null;
  // eCW's own print of the visit's note when stored, unless the box shows a
  // different note (the last one on file): the chip opens what the box says.
  const printed = !noteElsewhere && lastVisit?.original?.ref ? lastVisit.original : null;
  const noteDay = note?.service_date || lastVisit?.date;
  const toggleNote = () => {
    track(noteOpen ? "note_close" : "note_open");
    setNoteOpen((o) => !o);
  };
  const anchorRow = lastVisit ? (
    <li
      style={{
        ...styles.day,
        ...(activeFilter ? styles.dimmed : {}),
      }}
      data-anchor="last-visit"
      data-refs={[LAST_VISIT_REF, ...noteSources.map((src) => refToken(src.ref))].join(" ")}
    >
      <DateCell lines={dateLines(lastVisit.date || anchor?.date, referenceYear)} />
      <div style={{ ...styles.anchor, ...(flash === LAST_VISIT_REF ? styles.flash : {}) }}>
        <div
          className="pvb-head"
          data-can-open={noteMore}
          style={styles.rowHeadBox}
          onClick={noteMore ? (e) => headClick(e, toggleNote) : undefined}
        >
          <span style={styles.caretSlot}>{noteMore && <Caret open={noteOpen} />}</span>
          <div style={styles.rowBody}>
            {(printed || noteRef) && (
              <span style={styles.chipSlot}>
                <OriginalChip
                  media="note"
                  label={[
                    "Visit note",
                    noteDay ? dayLabel(noteDay) : "",
                    printed ? mediaWords("pdf", printed.pages) : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  onOpen={() =>
                    printed
                      ? openOriginal(printed.ref, "pdf", "note", "Visit note")
                      : openOriginal(noteRef as string, "note", "note", "Visit note")
                  }
                />
              </span>
            )}
            <span {...toggleProps(noteMore, noteOpen, noteId, toggleNote)} style={styles.anchorTitle}>
              {visitLabel}
            </span>
            {visitTail.length > 0 && <span style={styles.anchorSub}>{visitTail.join(" · ")}</span>}
          </div>
        </div>
        {noteOpen && noteMore && (
          <div id={noteId} style={styles.anchorNote}>
            {noteSummary && (
              <div>
                {note?.ai !== false && <AiMark />}
                {noteSummary}
              </div>
            )}
            {noteExcerpts.map((src) => (
              <div key={src.ref} style={styles.noteExcerpt}>
                “{pretty(src.detail).trim()}”
                <span style={styles.noteExcerptFrom}>
                  From the note{note?.service_date ? ` · ${dayLabel(note.service_date)}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </li>
  ) : !anchorDay ? (
    <li style={styles.day}>
      <span />
      <div style={styles.quietLine}>No earlier visit on record</div>
    </li>
  ) : null;

  const earlierDays = timeline.earlier.length + timeline.older.length;
  const earlierLabel = () => {
    const n = timeline.earlierCount;
    if (timeline.earlier.length && timeline.earlierSpan) {
      const [from, to] = timeline.earlierSpan;
      // "Sep 18 – 24" within one month of the visit's year; whole dates otherwise.
      const oneMonth = from.slice(0, 7) === to.slice(0, 7) && Number(from.slice(0, 4)) === referenceYear;
      const span =
        from === to
          ? dayLabel(from)
          : oneMonth
            ? `${dayLabel(from)} – ${Number(to.slice(8, 10))}`
            : `${dayLabel(from)} – ${dayLabel(to)}`;
      return (
        <>
          Show {n} earlier<span style={styles.moreSpan}> · {span}</span>
        </>
      );
    }
    return `Show ${n} older`;
  };
  const timelineId = `${idPrefix}-timeline`;
  const nothing = !timeline.pending.length && !timeline.shown.length && !earlierDays && Array.isArray(data.events);
  // Focused, the timeline shows only when it holds something of the tile's.
  const timelineSection =
    (lastVisit || Array.isArray(data.events)) && !(activeFilter && nothing) ? (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          Timeline
          {!anchorDay && <span style={styles.sectionNote}>Last 365 days</span>}
        </div>
        <ol id={timelineId} style={styles.days}>
          {timeline.pending.length > 0 && (
            <li style={styles.day} data-day="pending">
              <DateCell lines={["Pending"]} style={styles.pendingLabel} />
              <div style={styles.dayBody}>
                <ul style={styles.dayRows}>
                  {timeline.pending.map((event) => (
                    <li
                      key={event.ref}
                      data-kind="pending"
                      data-refs={refToken(event.ref)}
                      style={{ ...styles.eventRow, ...(flash === event.ref ? styles.flash : {}) }}
                    >
                      <div style={styles.rowHeadBox}>
                        <span style={styles.caretSlot} />
                        <span style={styles.rowText} title={event.name || undefined}>
                          {event.short || event.name || rowName(event)}
                          {event.date && (
                            <>
                              {" · "}
                              <span style={styles.noWrap}>ordered {dayLabel(event.date)}</span>
                            </>
                          )}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                {(timeline.pendingHidden > 0 || (allPending && timeline.pending.length > MAX_PENDING)) && (
                  <button
                    type="button"
                    className="pvb-more"
                    style={{ ...styles.moreLink, ...styles.moreInRail }}
                    onClick={() => {
                      track("pending_more", { expanded: !allPending, hidden: timeline.pendingHidden });
                      setAllPending((all) => !all);
                    }}
                  >
                    {allPending ? "Show less" : `Show ${timeline.pendingHidden} more pending`}
                  </button>
                )}
              </div>
            </li>
          )}
          {timeline.shown.map((d) => renderDay(d))}
          {earlierOpen && timeline.earlier.map((d) => renderDay(d))}
          {earlierDays > 0 && (
            <li style={styles.day}>
              <span />
              <button
                type="button"
                className="pvb-more"
                style={{ ...styles.moreLink, ...styles.moreInRail }}
                aria-expanded={earlierOpen}
                aria-controls={timelineId}
                onClick={() => {
                  track("timeline_earlier", { expanded: !earlierOpen, rows: timeline.earlierCount });
                  setEarlierOpen((o) => !o);
                }}
              >
                {earlierOpen ? "Show less" : earlierLabel()}
              </button>
            </li>
          )}
          {anchorRow}
          {earlierOpen && timeline.older.length > 0 && (
            <li style={styles.olderGroup} data-group="older">
              <div style={styles.olderLabel}>Older · arrived since</div>
              <ol style={styles.days}>{timeline.older.map((d) => renderDay(d, true))}</ol>
            </li>
          )}
        </ol>
        {nothing && !activeFilter && <div style={styles.quietLine}>Nothing new found in the records read.</div>}
      </div>
    ) : null;

  // ── Worth asking: the model's questions; a tap asks it in the chat ──
  const questions = (data.questions ?? []).filter((q) => !!q?.text?.trim());
  const worthAsking = questions.length ? (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>
        <AiMark style={styles.aiMarkInTitle} />
        Worth asking
      </div>
      <ul style={styles.questionList}>
        {questions.map((q, i) => {
          const text = pretty(q.text);
          return (
            <li key={`q${i}`}>
              {onAsk ? (
                <button
                  type="button"
                  style={{
                    ...styles.question,
                    ...styles.questionButton,
                    ...(askDisabled ? styles.buttonDisabled : {}),
                  }}
                  disabled={askDisabled}
                  onClick={() => {
                    track("question_ask", { index: i, of: questions.length });
                    onAsk(q.text);
                  }}
                >
                  <Glyph name="ask" />
                  <span style={styles.rowText}>{text}</span>
                </button>
              ) : (
                <div style={styles.question}>
                  <Glyph name="ask" />
                  <span style={styles.rowText}>{text}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {onAsk && <div style={styles.hint}>Tap a question to ask it in chat.</div>}
    </div>
  ) : null;

  const coverageGaps = (data.coverage?.gaps ?? []).filter((g) => !!g?.trim());
  const incomplete = data.coverage?.complete === false;
  const coverageText = pretty(coverageGaps.length ? coverageGaps.join("; ") : "some sources");

  // ── Actions ──
  const onRefresh = async () => {
    if (!clientId || frozen || refresh === "sending") return;
    track("refresh");
    setRefresh("sending");
    setRefresh(await requestPrechartRefresh(prechartBaseUrl(), clientId));
  };

  const onFeedback = async (helpful: boolean) => {
    if (frozen || feedback.phase === "sending" || feedback.phase === "sent") return;
    track("feedback", { helpful });
    setFeedback({ phase: "sending", helpful });
    const ok = await sendPrechartFeedback(prechartBaseUrl(), data.brief_id, helpful, clientId);
    setFeedback(ok ? { phase: "sent", helpful } : { phase: "error" });
  };

  // Only a failed request can be retried; queued / running / turned off stay put.
  const refreshLocked = refresh !== "idle" && refresh !== "error";
  const refreshLabel =
    refresh === "sending" ? "Refreshing…" : refresh === "queued" || refresh === "in_progress" ? "Queued" : "Refresh";
  const refreshMessage =
    refresh === "queued"
      ? "Refresh queued. The updated brief shows the next time you open this patient."
      : refresh === "in_progress"
        ? "A refresh is already running for this visit."
        : refresh === "disabled"
          ? "Pre-charting is turned off for you."
          : refresh === "error"
            ? "Couldn't queue a refresh. Try again."
            : "";
  const feedbackLocked = feedback.phase === "sending" || feedback.phase === "sent";
  const feedbackMessage =
    feedback.phase === "sent"
      ? "Thanks for the feedback."
      : feedback.phase === "error"
        ? "Couldn't send feedback. Try again."
        : "";

  // Two columns: the glance (summary, labs, questions) beside the timeline.
  const summaryShown = activeFilter ? null : summarySection;
  const questionsShown = activeFilter ? null : worthAsking;
  const columns = wide && !!(summaryShown || labsSection || questionsShown);

  // Collapsed: the summary and labs show through a blur, under "Show more".
  // Keyed so opening remounts it: React would otherwise reuse the peek body as
  // the left column, and the inert set below would leave that column dead.
  const peek = (
    <div key="peek" style={styles.peek}>
      <div style={styles.peekBody} aria-hidden="true" ref={(el) => el?.setAttribute("inert", "")}>
        {summarySection}
        {labsSection}
      </div>
      <div style={styles.peekVeil}>
        <button type="button" className="pvb-show-more" style={styles.showMore} onClick={() => expand("button")}>
          Show more
          <Caret open />
        </button>
      </div>
    </div>
  );

  return (
    <section
      ref={setCard}
      style={{ ...styles.container, ...(wide ? styles.containerWide : {}), ...(frozen ? styles.frozenBox : {}) }}
      aria-label="Prechart brief"
      data-layout={columns ? "columns" : "single"}
    >
      <style>{CARD_CSS}</style>
      <div style={styles.header}>
        <div style={{ ...styles.iconBadge, ...(frozen ? styles.iconBadgeFrozen : {}) }}>
          <ClipboardIcon />
        </div>
        <div style={styles.headerText}>
          <span style={styles.title}>Prechart brief</span>
          {apptLine && <span style={styles.meta}>{apptLine}</span>}
          {sinceLine}
        </div>
      </div>

      {frozen && <div style={styles.frozenCaption}>An updated brief is further down the thread.</div>}

      <div style={styles.tileBlock}>
        {tileStrip}
        {filterBar}
      </div>

      {!expanded ? (
        peek
      ) : columns ? (
        <div style={styles.columns}>
          <div style={styles.columnLeft} data-column="left">
            {summaryShown}
            {labsSection}
            {questionsShown}
          </div>
          <div style={styles.columnRight} data-column="right">
            {timelineSection}
          </div>
        </div>
      ) : (
        <>
          {summaryShown}
          {labsSection}
          {timelineSection}
          {questionsShown}
        </>
      )}

      {expanded && incomplete && (
        <div style={styles.coverage}>
          <b style={styles.coverageLabel}>Not summarised:</b> {coverageText}
          {coverageText.endsWith(".") ? "" : "."}
        </div>
      )}

      {!frozen && expanded && (
        <div style={styles.actions}>
          {clientId ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshLocked}
              style={{ ...styles.ghostButton, ...(refreshLocked ? styles.buttonDisabled : {}) }}
            >
              <ReloadOutlined spin={refresh === "sending"} style={{ fontSize: 12 }} />
              {refreshLabel}
            </button>
          ) : (
            <span />
          )}
          <div style={styles.feedback}>
            <button
              type="button"
              aria-label="Helpful"
              title="Helpful"
              aria-pressed={feedback.helpful === true && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(true)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === true && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== true ? styles.buttonDisabled : {}),
              }}
            >
              <LikeOutlined aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Not helpful"
              title="Not helpful"
              aria-pressed={feedback.helpful === false && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(false)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === false && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== false ? styles.buttonDisabled : {}),
              }}
            >
              <DislikeOutlined aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {!frozen && (refreshMessage || feedbackMessage) && (
        <div style={styles.status} role="status">
          {[refreshMessage, feedbackMessage].filter(Boolean).join(" ")}
        </div>
      )}
    </section>
  );
};

const listReset: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };
const linkButton: React.CSSProperties = {
  padding: 0,
  border: 0,
  backgroundColor: "transparent",
  fontFamily: "inherit",
  fontWeight: 500,
  color: "#2563EB",
  cursor: "pointer",
  textAlign: "left",
};
const ellipsis: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "16px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  containerWide: { padding: "20px 22px" },
  peek: { position: "relative", maxHeight: "172px", overflow: "hidden", marginTop: "-4px" },
  peekBody: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    filter: "blur(4px)",
    userSelect: "none",
    pointerEvents: "none",
  },
  peekVeil: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "6px",
    background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 40%, #FFFFFF 88%)",
  },
  showMore: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "32px",
    padding: "0 12px 0 16px",
    borderRadius: "999px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#EEF2FF",
    color: "#4338CA",
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
  },
  frozenBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none" },
  frozenCaption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconBadgeFrozen: { backgroundColor: "#F3F4F6", filter: "grayscale(1)" },
  headerText: { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, paddingTop: "3px" },
  title: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45 },
  meta: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  metaDate: { fontWeight: 600, color: "#374151" },
  plainNote: {
    fontSize: "11.5px",
    fontStyle: "italic",
    fontWeight: 400,
    color: "#9CA3AF",
    textTransform: "none",
    letterSpacing: 0,
    marginLeft: "4px",
  },

  aiMark: { display: "inline-flex", verticalAlign: "-1px", marginRight: "5px", color: "#6366F1" },
  aiMarkInTitle: { marginRight: 0 },

  // Tiles: always four, the same size; only the numbers change.
  tileBlock: { display: "flex", flexDirection: "column", gap: "8px" },
  tiles: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px" },
  tilesTwoByTwo: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  tile: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "1px",
    height: "86px",
    boxSizing: "border-box",
    padding: "8px 9px 7px",
    borderRadius: "10px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
    minWidth: 0,
    overflow: "hidden",
  },
  tileCount: {
    fontSize: "19px",
    fontWeight: 700,
    lineHeight: 1.15,
    fontVariantNumeric: "tabular-nums",
    color: "#374151",
  },
  tileLabel: {
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#374151",
    height: "27.5px",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  tileSub: { display: "flex", fontSize: "10.5px", lineHeight: 1.3, color: "#6B7280", minWidth: 0, marginTop: "auto" },
  tileNames: { ...ellipsis, minWidth: 0 },
  tileMore: { flexShrink: 0, whiteSpace: "pre" },
  tileNarrow: { padding: "8px 6px 7px" },
  tileLabelNarrow: { fontSize: "10.5px", letterSpacing: "-0.01em" },
  tileRed: { borderColor: "#FECACA", backgroundColor: "#FEF2F2", color: "#B91C1C" },
  tileAmber: { borderColor: "#FDE68A", backgroundColor: "#FFFBEB", color: "#B45309" },
  tileNeutral: {},
  tileZero: { borderColor: "#EEF0F3", backgroundColor: "#FCFCFD", color: "#A3A8B3", cursor: "default", opacity: 0.75 },
  // Indigo: the card's own state (blue means "jump to a fact").
  tileSelected: { boxShadow: "0 0 0 2px #6366F1" },
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#3730A3",
    backgroundColor: "#EEF2FF",
    border: "1px solid #C7D2FE",
    borderRadius: "8px",
    padding: "5px 6px 5px 10px",
  },
  filterLead: { fontWeight: 500, minWidth: 0 },
  filterClear: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    marginLeft: "auto",
    padding: "3px 8px",
    fontSize: "12px",
    fontWeight: 500,
    fontFamily: "inherit",
    color: "#4338CA",
    backgroundColor: "#FFFFFF",
    border: "1px solid #C7D2FE",
    borderRadius: "6px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  filterX: { fontSize: "14px", lineHeight: 1 },

  // Sections
  section: { display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#6B7280",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  sectionNote: { marginLeft: "auto", fontWeight: 500, textTransform: "none", letterSpacing: 0 },
  quietLine: { fontSize: "12.5px", color: "#6B7280", lineHeight: 1.45 },
  hint: { fontSize: "11.5px", color: "#9CA3AF", lineHeight: 1.4 },

  // Summary: tappable phrases are bold and blue.
  summary: { margin: 0, fontSize: "13.5px", lineHeight: 1.65, color: "#374151", overflowWrap: "anywhere" },
  phrase: {
    fontWeight: 600,
    color: "#2563EB",
    textDecoration: "underline",
    textDecorationColor: "#BFDBFE",
    textUnderlineOffset: "3px",
  },
  phraseWarn: {
    fontWeight: 600,
    color: "#B45309",
    textDecoration: "underline",
    textDecorationColor: "#FDE68A",
    textUnderlineOffset: "3px",
  },
  phraseButton: { cursor: "pointer" },
  identityList: { ...listReset, display: "flex", flexDirection: "column", gap: "6px" },
  identityLine: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    columnGap: "6px",
    alignItems: "start",
    fontSize: "12px",
    color: "#6B7280",
    lineHeight: 1.5,
  },

  glyph: { display: "block", width: "14px", height: "14px", marginTop: "3px", flexShrink: 0 },
  // A row: the caret's slot, then the title, its tags and the line under it.
  // The head is the tap target; the chip floats at its right edge.
  rowHeadBox: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    columnGap: "7px",
    alignItems: "start",
    padding: "3px 4px",
    margin: "-3px -4px",
  },
  caretSlot: { display: "block", width: "14px", minHeight: "14px", paddingTop: "3px" },
  caret: { display: "block", color: "#9CA3AF", transition: "transform 150ms ease" },
  caretOpen: { transform: "rotate(90deg)" },
  rowBody: { display: "block", minWidth: 0, overflowWrap: "anywhere" },
  chipSlot: { float: "right", marginLeft: "8px", marginTop: "1px" },
  originalChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "20px",
    boxSizing: "border-box",
    padding: "0 6px 0 7px",
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: 1,
    fontFamily: "inherit",
    color: "#4B5563",
    backgroundColor: "#FFFFFF",
    border: "1px solid #D9DCE3",
    borderRadius: "6px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  noWrap: { whiteSpace: "nowrap" },
  rowText: { minWidth: 0, overflowWrap: "anywhere" },
  tag: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  tagUnreviewed: { color: "#B45309", backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" },
  tagTeal: { color: "#0F766E", backgroundColor: "#F0FDFA", border: "1px solid #99F6E4" },
  tagInline: { display: "inline-block", marginLeft: "6px", verticalAlign: "1px" },
  dimmed: { opacity: 0.45 },
  // Where a jump lands: a blue ring and wash, as blue as the phrase tapped.
  flash: { outline: "2px solid #93C5FD", outlineOffset: "4px", borderRadius: "8px", backgroundColor: "#F5F9FF" },

  // Labs
  labs: { ...listReset, display: "flex", flexDirection: "column", gap: "12px" },
  lab: { display: "flex", flexDirection: "column", gap: "7px", minWidth: 0 },
  labNext: { borderTop: "1px solid #F3F4F6", paddingTop: "12px" },
  labHead: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "12.5px",
    color: "#6B7280",
    minWidth: 0,
    minHeight: "20px",
  },
  labChip: { marginLeft: "auto", flexShrink: 0 },
  labHeadSticky: { position: "sticky", top: 0, zIndex: 1, backgroundColor: "#FFFFFF" },
  labName: { ...ellipsis, fontWeight: 600, color: "#111827", minWidth: 0 },
  labDate: { whiteSpace: "nowrap", flexShrink: 0 },
  labMetaLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "-4px",
    paddingLeft: "21px",
    fontSize: "12px",
    color: "#6B7280",
  },
  chipLineWrap: { position: "relative", minWidth: 0 },
  chipRow: { display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "5px", overflow: "hidden", minWidth: 0 },
  measure: {
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    gap: "5px",
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    height: 0,
    overflow: "hidden",
  },
  chip: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "3px",
    boxSizing: "border-box",
    padding: "3px 7px",
    borderRadius: "7px",
    // Longhands: the flagged, more and normal chips swap one of them.
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    fontSize: "12px",
    lineHeight: 1.3,
    color: "#374151",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipFit: { maxWidth: "60%", flexShrink: 1, minWidth: 0 },
  chipFlagged: { borderColor: "#FECACA", backgroundColor: "#FEF2F2" },
  chipName: { ...ellipsis, minWidth: 0 },
  chipValue: { fontWeight: 700, color: "#B91C1C", flexShrink: 0 },
  chipMore: { borderColor: "#FECACA", backgroundColor: "#FEF2F2", color: "#B91C1C", fontWeight: 600 },
  chipNormal: { borderStyle: "dashed", color: "#6B7280" },
  flagBadge: {
    fontSize: "10px",
    fontWeight: 700,
    lineHeight: 1.2,
    padding: "1px 4px",
    marginLeft: "3px",
    borderRadius: "4px",
    color: "#B91C1C",
    backgroundColor: "#FEE2E2",
    flexShrink: 0,
  },
  tableBox: { border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden", backgroundColor: "#FFFFFF" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "12.5px" },
  th: {
    textAlign: "left",
    fontWeight: 600,
    color: "#6B7280",
    fontSize: "10.5px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    padding: "6px 10px",
    borderBottom: "1px solid #F0F1F5",
  },
  thRight: { textAlign: "right" },
  td: { padding: "6px 10px", borderBottom: "1px solid #F3F4F6", color: "#374151", fontVariantNumeric: "tabular-nums" },
  tdValue: { textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" },
  tdValueFlagged: { color: "#B91C1C" },
  tdUnitsInline: { fontWeight: 400, color: "#6B7280", fontSize: "11px" },
  tdRange: { color: "#6B7280" },
  tdReport: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.45, color: "#374151" },
  trFlagged: { backgroundColor: "#FEF7F7" },
  tableFoot: {
    padding: "6px 10px",
    fontSize: "11.5px",
    color: "#6B7280",
    backgroundColor: "#FCFCFD",
    borderTop: "1px solid #F0F1F5",
  },
  reportBox: {
    fontSize: "12.5px",
    color: "#374151",
    lineHeight: 1.5,
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    whiteSpace: "pre-wrap",
  },
  reportFoot: { marginTop: "6px", fontSize: "11.5px", color: "#6B7280" },
  // "Show …" lines: grey, the one word for revealing more.
  moreLink: { ...linkButton, alignSelf: "flex-start", fontSize: "12.5px", lineHeight: 1.4, color: "#6B7280" },
  // In the timeline: aligned with the rows' carets.
  moreInRail: { paddingLeft: "14px" },
  moreSpan: { color: "#9CA3AF", fontWeight: 400 },

  // Timeline: date | rule | rows
  days: { ...listReset, display: "flex", flexDirection: "column", gap: "14px" },
  day: { display: "grid", gridTemplateColumns: "52px minmax(0, 1fr)", columnGap: "10px" },
  dayDate: {
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: "#4B5563",
    lineHeight: 1.5,
    paddingTop: "1px",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  dayYear: { display: "block", fontWeight: 500, color: "#9CA3AF", fontSize: "11px" },
  dayDateOlder: { color: "#9CA3AF" },
  pendingLabel: { color: "#9CA3AF" },
  dayBody: { display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 },
  dayRows: {
    ...listReset,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
    borderLeft: "2px solid #F3F4F6",
    paddingLeft: "12px",
  },
  eventRow: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    fontSize: "13px",
    color: "#374151",
    lineHeight: 1.5,
  },
  rowTitle: { fontWeight: 600, color: "#111827" },
  rowTitleQuiet: { color: "#4B5563" },
  // Two lines at most: the gist at a glance; the row unfolds to the rest.
  rowSub: {
    fontSize: "12px",
    color: "#6B7280",
    lineHeight: 1.45,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  // A row with nothing more to show shows its line whole.
  rowSubWhole: { display: "block", fontSize: "12px", color: "#6B7280", lineHeight: 1.45 },
  // What a row unfolds to, under its title.
  unfolded: { marginLeft: "21px", marginTop: "6px", minWidth: 0 },
  gist: { display: "flex", flexDirection: "column", gap: "6px" },
  gistLabel: { fontWeight: 600, color: "#111827" },
  members: { ...listReset, display: "flex", flexDirection: "column", gap: "8px" },
  member: { display: "block", minWidth: 0, fontSize: "12.5px", lineHeight: 1.45, overflowWrap: "anywhere" },
  memberTitle: { fontWeight: 600, color: "#1F2937" },
  // Two lines, like a row's: the original is one tap away for the rest.
  memberSub: {
    fontSize: "12px",
    color: "#6B7280",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  rowDetail: {
    fontSize: "12px",
    color: "#4B5563",
    lineHeight: 1.45,
    padding: "6px 9px",
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
    borderLeft: "2px solid #C7D2FE",
    whiteSpace: "pre-wrap",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  detailLabel: { fontWeight: 600, color: "#374151" },
  labLine: { fontSize: "12.5px", color: "#6B7280", minWidth: 0, lineHeight: 1.5 },
  labLineJump: {
    ...linkButton,
    fontSize: "12.5px",
    fontWeight: 600,
    lineHeight: "inherit",
    textDecoration: "underline",
    textDecorationColor: "#BFDBFE",
    textUnderlineOffset: "3px",
  },
  anchor: {
    padding: "8px 10px",
    borderRadius: "9px",
    backgroundColor: "#F8F9FF",
    border: "1px solid #E0E7FF",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "#374151",
    overflowWrap: "anywhere",
  },
  anchorTitle: { fontWeight: 600, color: "#111827" },
  anchorSub: { display: "block", fontSize: "12px", color: "#6B7280", lineHeight: 1.45 },
  anchorNote: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginTop: "8px",
    marginLeft: "21px",
    paddingTop: "8px",
    borderTop: "1px solid #E0E7FF",
    fontSize: "12.5px",
    color: "#374151",
    lineHeight: 1.5,
  },
  noteExcerpt: {
    fontSize: "12px",
    color: "#4B5563",
    lineHeight: 1.5,
    paddingLeft: "9px",
    borderLeft: "2px solid #C7D2FE",
    whiteSpace: "pre-wrap",
  },
  noteExcerptFrom: { display: "block", marginTop: "2px", fontSize: "11px", color: "#9CA3AF" },
  olderGroup: { display: "flex", flexDirection: "column", gap: "10px" },
  olderLabel: {
    fontSize: "11px",
    color: "#9CA3AF",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },

  // Worth asking
  questionList: { ...listReset, display: "flex", flexDirection: "column", gap: "4px" },
  question: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    columnGap: "8px",
    alignItems: "start",
    width: "100%",
    fontSize: "12.5px",
    color: "#4B5563",
    lineHeight: 1.5,
    padding: "3px 0",
  },
  questionButton: {
    border: 0,
    backgroundColor: "transparent",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },

  // The wide layout
  columns: { display: "grid", gridTemplateColumns: "340px minmax(0, 1fr)", columnGap: "32px", alignItems: "start" },
  columnLeft: { display: "flex", flexDirection: "column", gap: "20px", minWidth: 0, position: "sticky", top: "16px" },
  columnRight: { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 },

  coverage: { fontSize: "11.5px", color: "#9CA3AF", lineHeight: 1.45, paddingTop: "4px" },
  coverageLabel: { fontWeight: 600, color: "#6B7280" },

  // Actions
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    borderTop: "1px solid #F0F1F5",
    paddingTop: "8px",
  },
  ghostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    fontSize: "12.5px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #D6D9E0",
    backgroundColor: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  feedback: { display: "flex", gap: "4px" },
  thumb: {
    width: "30px",
    height: "28px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    borderRadius: "8px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "transparent",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1,
  },
  thumbPicked: { borderColor: "#C7D2FE", backgroundColor: "#EEF2FF", cursor: "default" },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
  status: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
};

export default PreVisitBrief;
